import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import {
  Megaphone, Users, Send, Mail, Plus, FileText, Settings as SettingsIcon,
  Loader2, CheckCircle2, AlertTriangle, Inbox, ExternalLink, Trash2,
  Eye, UserPlus, Archive, Clock, X, ChevronRight, Sparkles, Star, Lock,
} from "lucide-react";
import { marketingApi, formsApi, customersApi, brandingApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import EmailBuilder from "../components/marketing/EmailBuilder";
import CampaignDetail from "../components/marketing/CampaignDetail";
import FormBuilder from "../components/marketing/FormBuilder";
import LeadsInbox from "../components/marketing/LeadsInbox";
import ReputationManagementTab from "../components/marketing/ReputationManagementTab";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";
import { renderEmailHtml } from "../components/marketing/emailBlocks";
import { toast } from "sonner";

const SAMPLE = { firstName: "Jordan", lastName: "Lee", customerName: "Jordan Lee", companyName: "Your Company", lastServiceDate: "May 12, 2026", reviewLink: "https://g.page/review" };
const fillSample = (html) => (html || "").replace(/\{\{(\w+)\}\}/g, (_, k) => (SAMPLE[k] != null ? SAMPLE[k] : `{{${k}}}`));

function StatCard({ icon: Icon, label, value, tone = "blue", testid }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600", amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-4" data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-4.5 w-4.5" /></div>
      <div className="text-[24px] font-semibold text-slate-900 mt-2.5 leading-none">{value}</div>
      <div className="text-[12px] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function CampStatus({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-600", sending: "bg-blue-100 text-blue-700",
    sent: "bg-emerald-100 text-emerald-700", failed: "bg-red-100 text-red-700",
  };
  return <span className={`inline-flex h-5 px-2 items-center rounded-full text-[10.5px] font-semibold capitalize ${map[status] || map.draft}`}>{status}</span>;
}

function LeadStatus({ status }) {
  const map = { new: "bg-blue-100 text-blue-700", converted: "bg-emerald-100 text-emerald-700", archived: "bg-slate-100 text-slate-500" };
  return <span className={`inline-flex h-5 px-2 items-center rounded-full text-[10.5px] font-semibold capitalize ${map[status] || map.new}`}>{status || "new"}</span>;
}

function Thumb({ src, icon: Icon = Megaphone, color = "#2563EB" }) {
  if (src) return <img src={src} alt="" className="h-12 w-12 rounded-lg object-cover border border-slate-200 shrink-0" />;
  return <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: color }}><Icon className="h-5 w-5" /></div>;
}

function RateBar({ value, label }) {
  return (
    <div className="w-full max-w-[260px]">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-slate-500">{label}</span>
        <span className="font-semibold text-slate-700">{value}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  );
}

function PreviewShell({ title, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden flex flex-col" data-testid={testid} style={{ minHeight: 460 }}>
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 shrink-0">
        <Eye className="h-4 w-4 text-slate-400" />
        <span className="text-[11.5px] font-semibold text-slate-500 uppercase tracking-wide">{title}</span>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  );
}

function PreviewEmpty({ icon: Icon = Eye, text }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-400">
      <Icon className="h-8 w-8 mb-2" />
      <p className="text-[13px]">{text}</p>
    </div>
  );
}

function NoDesign({ onOpen }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
      <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3"><Megaphone className="h-6 w-6 text-blue-600" /></div>
      <h3 className="text-[14px] font-semibold text-slate-800">No visual design yet</h3>
      <p className="text-[12.5px] text-slate-500 mt-1 max-w-xs">This campaign uses a plain message. Open the editor to build a designed email.</p>
      <Button onClick={onOpen} data-testid="preview-open-editor" className="mt-4 h-8 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[13px]">Open editor</Button>
    </div>
  );
}

function FormMini({ form }) {
  if (!form) return null;
  const accent = form.accentColor || "#2563EB";
  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden", maxWidth: 420, margin: "0 auto", boxShadow: "0 6px 20px rgba(0,0,0,0.25)" }}>
        <div style={{ height: 8, background: accent }} />
        <div style={{ padding: 18 }}>
          {form.logoUrl ? <img src={form.logoUrl} alt="" style={{ height: 32, marginBottom: 10 }} /> : null}
          <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{form.title || form.name}</div>
          {form.intro ? <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, lineHeight: 1.5 }}>{form.intro}</div> : null}
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            {(form.fields || []).map((fl) => (
              <div key={fl.id}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 5 }}>{fl.label}{fl.required ? <span style={{ color: "#f43f5e" }}> *</span> : null}</div>
                {fl.type === "textarea"
                  ? <div style={{ height: 60, borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc" }} />
                  : <div style={{ height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc" }} />}
              </div>
            ))}
          </div>
          <div style={{ height: 40, borderRadius: 8, marginTop: 16, background: accent, color: "#fff", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{form.submitLabel || "Submit"}</div>
        </div>
      </div>
    </div>
  );
}

const flattenVal = (val) => {
  if (val == null) return "";
  if (typeof val === "string" || typeof val === "number") return String(val);
  if (Array.isArray(val)) return val.map(flattenVal).filter(Boolean).join(", ");
  if (typeof val === "object") return Object.values(val).map(flattenVal).filter(Boolean).join(" ");
  return String(val);
};
const fmtLabel = (k) => k.replace(/^fld[-_]?/i, "").replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim() || k;
const pickByKey = (s, re) => {
  for (const [k, val] of Object.entries(s.values || {})) if (re.test(k)) { const f = flattenVal(val); if (f) return f; }
  return "";
};
const leadEmail = (s) => {
  const direct = pickByKey(s, /e-?mail/i);
  if (direct) return direct;
  for (const val of Object.values(s.values || {})) { const f = flattenVal(val); if (/@/.test(f) && /\./.test(f)) return f; }
  return "";
};
const leadName = (s) => pickByKey(s, /name/i) || leadEmail(s) || "Lead";

function LeadPreview({ lead, onConvert, onArchive, onOpenCustomer, busy }) {
  if (!lead) return <PreviewEmpty icon={Inbox} text="Hover a lead to see the full submission" />;
  const v = lead.values || {};
  const entries = Object.entries(v).map(([k, val]) => [k, flattenVal(val)]).filter(([, val]) => val.trim() !== "");
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="p-4 overflow-y-auto flex-1">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[16px] font-semibold text-slate-900 truncate">{leadName(lead)}</div>
            <div className="text-[12.5px] text-slate-500 truncate">{leadEmail(lead) || "No email"}</div>
          </div>
          <LeadStatus status={lead.status} />
        </div>
        <div className="text-[11.5px] text-slate-400 mt-1 flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5" /> {lead.formName} · <Clock className="h-3.5 w-3.5" /> {lead.createdAt ? new Date(lead.createdAt).toLocaleString() : ""}
        </div>
        <div className="mt-4 rounded-xl border border-slate-200/80 divide-y divide-slate-100" data-testid="lead-fields">
          {entries.length === 0 ? <div className="px-3 py-3 text-[13px] text-slate-400">No fields submitted</div> :
            entries.map(([k, val]) => (
              <div key={k} className="px-3 py-2.5">
                <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">{fmtLabel(k)}</div>
                <div className="text-[13.5px] text-slate-700 mt-0.5 break-words">{val}</div>
              </div>
            ))}
        </div>
      </div>
      <div className="p-3 border-t border-slate-100 flex items-center gap-2 shrink-0">
        {lead.status === "converted" && lead.customerId ? (
          <Button onClick={() => onOpenCustomer(lead.customerId)} data-testid="lead-open-customer" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[13px] flex-1">
            <Users className="h-4 w-4" /> Open customer
          </Button>
        ) : (
          <Button onClick={() => onConvert(lead)} disabled={busy} data-testid="lead-convert" className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] flex-1">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Convert to customer
          </Button>
        )}
        {lead.status !== "archived" && (
          <Button onClick={() => onArchive(lead)} disabled={busy} variant="outline" data-testid="lead-archive" className="h-9 gap-1.5 text-[13px]">
            <Archive className="h-4 w-4" /> Archive
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Marketing({ embedded = false, initialTab = "campaigns", restrictTabs = null }) {
  const { user, hasModule } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [forms, setForms] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editCampaignId, setEditCampaignId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [formId, setFormId] = useState(null);
  const [leadsForm, setLeadsForm] = useState(null);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [reviewUrl, setReviewUrl] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  const [selCampaigns, setSelCampaigns] = useState([]);
  const [selForms, setSelForms] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [startWithAI, setStartWithAI] = useState(false);

  // preview state
  const [hoverCampaign, setHoverCampaign] = useState(null);
  const [campDesigns, setCampDesigns] = useState({});
  const [campLoading, setCampLoading] = useState(false);
  const [hoverForm, setHoverForm] = useState(null);
  const [formCache, setFormCache] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [hoverLead, setHoverLead] = useState(null);
  const [leadBusy, setLeadBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [ov, cps, fs] = await Promise.all([
        marketingApi.overview().catch(() => null),
        marketingApi.listCampaigns().catch(() => []),
        formsApi.list().catch(() => []),
      ]);
      setOverview(ov);
      setCampaigns(cps);
      setForms(fs);
      setSelCampaigns((s) => s.filter((id) => cps.some((c) => c.id === id)));
      setSelForms((s) => s.filter((id) => fs.some((f) => f.id === id)));
      if (ov) setReviewUrl(ov.googleReviewUrl || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      customersApi.list().then(setCustomers).catch(() => {});
      brandingApi.get().then(setBranding).catch(() => {});
    })();
  }, [refresh]);

  const loadLeads = useCallback(async (formsList) => {
    setLeadsLoading(true);
    try {
      const lists = await Promise.all((formsList || []).map((f) =>
        formsApi.submissions(f.id).then((subs) => (subs || []).map((s) => ({ ...s, formName: f.name, formId: f.id }))).catch(() => [])
      ));
      const all = lists.flat().sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
      setLeads(all);
    } finally { setLeadsLoading(false); }
  }, []);

  useEffect(() => { if (tab === "leads") loadLeads(forms); }, [tab, forms, loadLeads]);

  // default hover selections
  useEffect(() => { if (tab === "campaigns" && campaigns.length && !campaigns.some((c) => c.id === hoverCampaign)) setHoverCampaign(campaigns[0].id); }, [tab, campaigns, hoverCampaign]);
  useEffect(() => { if (tab === "forms" && forms.length && !forms.some((f) => f.id === hoverForm)) setHoverForm(forms[0].id); }, [tab, forms, hoverForm]);
  useEffect(() => { if (tab === "leads" && leads.length && !leads.some((l) => l.id === hoverLead)) setHoverLead(leads[0].id); }, [tab, leads, hoverLead]);

  // lazy-load campaign design for preview
  useEffect(() => {
    const id = hoverCampaign;
    if (!id) return;
    const c = campaigns.find((x) => x.id === id);
    if (!c || !c.designed || campDesigns[id]) return;
    setCampLoading(true);
    marketingApi.getCampaign(id).then((full) => setCampDesigns((p) => ({ ...p, [id]: full.design }))).catch(() => {}).finally(() => setCampLoading(false));
  }, [hoverCampaign, campaigns, campDesigns]);

  // lazy-load form for preview
  useEffect(() => {
    const id = hoverForm;
    if (!id || formCache[id]) return;
    setFormLoading(true);
    formsApi.get(id).then((full) => setFormCache((p) => ({ ...p, [id]: full }))).catch(() => {}).finally(() => setFormLoading(false));
  }, [hoverForm, formCache]);

  const smtpConfigured = !!overview?.smtpConfigured;

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await marketingApi.updateSettings({ googleReviewUrl: reviewUrl });
      toast.success("Marketing settings saved");
      setSettingsOpen(false);
    } catch (e) {
      toast.error("Failed to save settings.");
    } finally { setSavingSettings(false); }
  };

  const newForm = async () => {
    try {
      const f = await formsApi.create("New form");
      await refresh();
      setFormId(f.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create form."); }
  };

  const toggleId = (setFn) => (id) => setFn((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleCampaign = toggleId(setSelCampaigns);
  const toggleForm = toggleId(setSelForms);

  const deleteSelectedCampaigns = async () => {
    if (selCampaigns.length === 0) return;
    if (!window.confirm("Are you sure you want to DELETE?")) return;
    setDeleting(true);
    try {
      const results = await Promise.allSettled(selCampaigns.map((id) => marketingApi.deleteCampaign(id)));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) toast.error(`${failed} campaign${failed === 1 ? "" : "s"} couldn't be deleted (a sending campaign can't be removed).`);
      else toast.success(`Deleted ${selCampaigns.length} campaign${selCampaigns.length === 1 ? "" : "s"}`);
      setSelCampaigns([]);
      await refresh();
    } finally { setDeleting(false); }
  };

  const deleteSelectedForms = async () => {
    if (selForms.length === 0) return;
    if (!window.confirm("Are you sure you want to DELETE?")) return;
    setDeleting(true);
    try {
      await Promise.allSettled(selForms.map((id) => formsApi.remove(id)));
      toast.success(`Deleted ${selForms.length} form${selForms.length === 1 ? "" : "s"}`);
      setSelForms([]);
      await refresh();
    } finally { setDeleting(false); }
  };

  const convertLead = async (lead) => {
    setLeadBusy(true);
    try {
      await formsApi.convertLead(lead.id);
      toast.success(`${leadName(lead)} converted to a customer`);
      await loadLeads(forms);
      customersApi.list().then(setCustomers).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to convert lead."); }
    finally { setLeadBusy(false); }
  };
  const archiveLead = async (lead) => {
    setLeadBusy(true);
    try {
      await formsApi.archiveLead(lead.id);
      toast.success("Lead archived");
      await loadLeads(forms);
    } catch (e) { toast.error("Failed to archive lead."); }
    finally { setLeadBusy(false); }
  };

  const ALL_TABS = [
    ...(hasModule("email_builder") ? [["campaigns", "Campaigns", Megaphone]] : []),
    ...(hasModule("form_builder") ? [["forms", "Forms", FileText]] : []),
    ["leads", "Leads", Inbox],
    ["reputation", "Reputation Management", Star],
  ];
  const TABS = restrictTabs ? ALL_TABS.filter(([k]) => restrictTabs.includes(k)) : ALL_TABS;
  const tabKeys = TABS.map(([k]) => k).join(",");
  useEffect(() => {
    if (TABS.length && !TABS.some(([k]) => k === tab)) setTab(TABS[0][0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabKeys]);

  const hc = campaigns.find((x) => x.id === hoverCampaign);
  const hf = formCache[hoverForm];
  const hl = leads.find((x) => x.id === hoverLead);

  usePageHeader({
    title: "Marketing",
    subtitle: "Email campaigns, lead-capture forms & leads",
  }, !embedded);

  return (
    <>
      <div className={embedded ? "space-y-6" : "p-6 space-y-6"} data-testid="marketing-page">
        {/* Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Reachable customers (with email)" value={overview?.reachableCustomers ?? "—"} tone="blue" testid="stat-reachable" />
          <StatCard icon={Megaphone} label="Campaigns sent" value={overview?.campaignsSent ?? "—"} tone="violet" testid="stat-campaigns" />
          <StatCard icon={Send} label="Emails delivered" value={overview?.emailsDelivered ?? "—"} tone="emerald" testid="stat-delivered" />
          <div className="bg-white rounded-xl border border-slate-200/80 p-4" data-testid="stat-smtp">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${smtpConfigured ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}><Mail className="h-4.5 w-4.5" /></div>
            <div className="text-[14px] font-semibold text-slate-900 mt-2.5 flex items-center gap-1.5">
              {smtpConfigured ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Connected</> : <><AlertTriangle className="h-4 w-4 text-amber-500" /> Not connected</>}
            </div>
            <div className="text-[12px] text-slate-500 mt-1">{smtpConfigured ? "Your email is ready to send" : "Set up SMTP in Company Settings"}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between border-b border-slate-200">
          <div className="flex items-center gap-5">
            {TABS.map(([k, label, Icon]) => {
              const locked = k === "reputation" && !hasModule("reputation");
              const isReputation = k === "reputation";
              return (
                <button key={k} onClick={() => { setTab(k); if (!locked) refresh(); }} data-testid={`marketing-tab-${k}`}
                  style={isReputation ? { color: "#FBBF24" } : undefined}
                  className={`flex items-center gap-1.5 pb-2.5 text-[13.5px] font-medium border-b-2 transition-colors ${tab === k ? "border-blue-600" : "border-transparent"} ${isReputation ? "" : tab === k ? "text-blue-700" : "text-slate-500 hover:text-slate-700"} ${locked ? "opacity-50" : ""}`}>
                  <Icon className="h-4 w-4" /> {label}
                  {locked && <Lock className="h-3 w-3 ml-0.5" />}
                </button>
              );
            })}
          </div>
          <button onClick={() => setSettingsOpen(true)} data-testid="marketing-tab-settings"
            className="flex items-center gap-1.5 pb-2.5 text-[13px] font-medium text-slate-400 hover:text-slate-600 transition-colors">
            <SettingsIcon className="h-4 w-4" /> Settings
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
        ) : tab === "campaigns" ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] text-slate-500">Reach your customers with personalized email blasts. Hover to preview, click a name to edit.</p>
              <div className="flex items-center gap-2">
                {selCampaigns.length > 0 && (
                  <Button onClick={deleteSelectedCampaigns} disabled={deleting} data-testid="delete-campaigns-btn" variant="outline" className="h-9 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete ({selCampaigns.length})
                  </Button>
                )}
                {hasModule("ai_email_designer") && (
                  <Button onClick={() => { setEditCampaignId(null); setStartWithAI(true); setEditorOpen(true); }} data-testid="design-with-ai-btn" className="h-9 gap-1.5 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white"><Sparkles className="h-4 w-4" /> Design with AI</Button>
                )}
                <Button onClick={() => { setEditCampaignId(null); setStartWithAI(false); setEditorOpen(true); }} data-testid="new-campaign-btn" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4" /> New campaign</Button>
              </div>
            </div>

            {campaigns.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200/80 p-12 flex flex-col items-center text-center" data-testid="campaigns-empty">
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3"><Megaphone className="h-6 w-6 text-blue-600" /></div>
                <h3 className="text-[15px] font-semibold text-slate-900">No campaigns yet</h3>
                <p className="text-[13px] text-slate-500 mt-1 max-w-sm">Create your first email campaign to win back lapsed customers, request reviews, or promote a seasonal offer.</p>
                <Button onClick={() => setEditorOpen(true)} data-testid="campaigns-empty-cta" className="mt-4 h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4" /> New campaign</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(360px,420px)] gap-4 items-start">
                <div className="bg-white rounded-xl border border-slate-200/80 divide-y divide-slate-100 overflow-hidden" data-testid="campaigns-list">
                  {campaigns.map((c) => {
                    const pct = c.recipientCount ? Math.round(((c.deliveredCount || 0) / c.recipientCount) * 100) : 0;
                    const sent = c.status === "sent" || (c.recipientCount || 0) > 0;
                    return (
                      <div key={c.id} data-testid={`campaign-row-${c.id}`} onMouseEnter={() => setHoverCampaign(c.id)} onClick={() => setHoverCampaign(c.id)}
                        className={`flex items-center gap-3 px-3.5 py-3 transition-colors cursor-pointer ${hoverCampaign === c.id ? "bg-blue-50/60" : "hover:bg-slate-50"}`}>
                        <input type="checkbox" checked={selCampaigns.includes(c.id)} onChange={() => toggleCampaign(c.id)} onClick={(e) => e.stopPropagation()} data-testid={`campaign-select-${c.id}`} className="accent-blue-600 h-4 w-4 shrink-0 cursor-pointer" aria-label={`Select ${c.name}`} />
                        <Thumb src={c.thumbnailUrl} icon={Megaphone} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Draft → open the email builder for editing.
                                // Sent / sending / failed → open the analytics drawer.
                                if (c.status === "draft") {
                                  setEditCampaignId(c.id);
                                  setStartWithAI(false);
                                  setEditorOpen(true);
                                } else {
                                  setDetailId(c.id);
                                }
                              }}
                              data-testid={`campaign-open-${c.id}`}
                              title={c.status === "draft" ? "Edit draft" : "View campaign details"}
                              className="text-[14px] font-semibold text-slate-800 hover:text-blue-700 hover:underline truncate text-left"
                            >
                              {c.name}
                            </button>
                            <CampStatus status={c.status} />
                          </div>
                          <div className="text-[12px] text-slate-500 truncate mt-0.5">{c.subject}</div>
                          <div className="mt-2">{sent ? <RateBar value={pct} label={`Delivered ${c.deliveredCount || 0}/${c.recipientCount || 0}`} /> : <div className="text-[11px] text-slate-400">Not sent yet</div>}</div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                      </div>
                    );
                  })}
                </div>
                <div className="lg:sticky lg:top-4">
                  <PreviewShell title="Email preview" testid="campaign-preview-pane">
                    {!hc ? <PreviewEmpty text="Hover a campaign to preview its email" /> :
                      !hc.designed ? <NoDesign onOpen={() => { setEditCampaignId(hc.id); setStartWithAI(false); setEditorOpen(true); }} /> :
                        (campLoading && !campDesigns[hc.id]) ? <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> :
                          campDesigns[hc.id] ? <iframe title="campaign-preview" srcDoc={fillSample(renderEmailHtml(campDesigns[hc.id]))} className="w-full flex-1 min-h-[420px] bg-white" /> :
                            <PreviewEmpty text="Preview unavailable" />}
                  </PreviewShell>
                </div>
              </div>
            )}
          </div>
        ) : tab === "forms" ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] text-slate-500">Build lead-capture forms for your website or ads. Hover to preview, click a name to edit.</p>
              <div className="flex items-center gap-2">
                {selForms.length > 0 && (
                  <Button onClick={deleteSelectedForms} disabled={deleting} data-testid="delete-forms-btn" variant="outline" className="h-9 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete ({selForms.length})
                  </Button>
                )}
                <Button onClick={newForm} data-testid="new-form-btn" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4" /> New form</Button>
              </div>
            </div>

            {forms.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200/80 p-12 flex flex-col items-center text-center" data-testid="forms-empty">
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3"><Inbox className="h-6 w-6 text-blue-600" /></div>
                <h3 className="text-[15px] font-semibold text-slate-900">No forms yet</h3>
                <p className="text-[13px] text-slate-500 mt-1 max-w-sm">Create a form to capture leads from your website or ads — submissions land in your customer database automatically.</p>
                <Button onClick={newForm} data-testid="forms-empty-cta" className="mt-4 h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4" /> New form</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(360px,420px)] gap-4 items-start">
                <div className="bg-white rounded-xl border border-slate-200/80 divide-y divide-slate-100 overflow-hidden" data-testid="forms-list">
                  {forms.map((f) => (
                    <div key={f.id} data-testid={`form-row-${f.id}`} onMouseEnter={() => setHoverForm(f.id)} onClick={() => setHoverForm(f.id)}
                      className={`flex items-center gap-3 px-3.5 py-3 transition-colors cursor-pointer ${hoverForm === f.id ? "bg-blue-50/60" : "hover:bg-slate-50"}`}>
                      <input type="checkbox" checked={selForms.includes(f.id)} onChange={() => toggleForm(f.id)} onClick={(e) => e.stopPropagation()} data-testid={`form-select-${f.id}`} className="accent-blue-600 h-4 w-4 shrink-0 cursor-pointer" aria-label={`Select ${f.name}`} />
                      <Thumb icon={FileText} color={f.accentColor || "#2563EB"} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); setFormId(f.id); }} data-testid={`form-edit-${f.id}`} className="text-[14px] font-semibold text-slate-800 hover:text-blue-700 hover:underline truncate text-left">{f.name}</button>
                          <span className={`inline-flex h-5 px-2 items-center rounded-full text-[10.5px] font-semibold ${f.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{f.status === "active" ? "Active" : "Disabled"}</span>
                        </div>
                        <div className="text-[12px] text-slate-500 truncate mt-0.5">{f.title}</div>
                        <div className="flex items-center gap-3 mt-1.5 text-[11.5px] text-slate-500">
                          <span>{f.fieldCount} fields</span>
                          <span className="flex items-center gap-1"><Inbox className="h-3.5 w-3.5 text-slate-400" /> {f.submissionCount} leads</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setLeadsForm(f)} data-testid={`form-leads-${f.id}`} title="View leads" className="h-8 w-8 rounded-md text-slate-500 hover:bg-slate-100 flex items-center justify-center"><Inbox className="h-4 w-4" /></button>
                        <button onClick={() => window.open(`/f/${f.id}`, "_blank")} data-testid={`form-open-${f.id}`} title="Open public form" className="h-8 w-8 rounded-md text-slate-500 hover:bg-slate-100 flex items-center justify-center"><ExternalLink className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lg:sticky lg:top-4">
                  <PreviewShell title="Form preview" testid="form-preview-pane">
                    {!hoverForm ? <PreviewEmpty icon={FileText} text="Hover a form to preview it" /> :
                      (formLoading && !hf) ? <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> :
                        hf ? <FormMini form={hf} /> : <PreviewEmpty icon={FileText} text="Preview unavailable" />}
                  </PreviewShell>
                </div>
              </div>
            )}
          </div>
        ) : tab === "leads" ? (
          /* LEADS */
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] text-slate-500">Every submission from your forms. Hover to view, then convert or archive.</p>
              <div className="text-[12px] text-slate-400">{leads.length} total</div>
            </div>

            {leadsLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : leads.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200/80 p-12 flex flex-col items-center text-center" data-testid="leads-empty">
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3"><Inbox className="h-6 w-6 text-blue-600" /></div>
                <h3 className="text-[15px] font-semibold text-slate-900">No leads yet</h3>
                <p className="text-[13px] text-slate-500 mt-1 max-w-sm">When someone submits one of your forms, their details will show up here for you to convert into a customer.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(360px,420px)] gap-4 items-start">
                <div className="bg-white rounded-xl border border-slate-200/80 divide-y divide-slate-100 overflow-hidden" data-testid="leads-list">
                  {leads.map((l) => (
                    <div key={l.id} data-testid={`lead-row-${l.id}`} onMouseEnter={() => setHoverLead(l.id)} onClick={() => setHoverLead(l.id)}
                      className={`flex items-center gap-3 px-3.5 py-3 transition-colors cursor-pointer ${hoverLead === l.id ? "bg-blue-50/60" : "hover:bg-slate-50"}`}>
                      <div className="h-10 w-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[13px] font-semibold shrink-0">{leadName(l).charAt(0).toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); if (l.status === "converted" && l.customerId) setOpenCustomerId(l.customerId); else setHoverLead(l.id); }} data-testid={`lead-open-${l.id}`} className="text-[14px] font-semibold text-slate-800 hover:text-blue-700 hover:underline truncate text-left">{leadName(l)}</button>
                          <LeadStatus status={l.status} />
                        </div>
                        <div className="text-[12px] text-slate-500 truncate mt-0.5">{leadEmail(l) || "No email"}</div>
                        <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5 truncate"><FileText className="h-3 w-3" /> {l.formName}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                    </div>
                  ))}
                </div>
                <div className="lg:sticky lg:top-4">
                  <PreviewShell title="Lead details" testid="lead-preview-pane">
                    <LeadPreview lead={hl} onConvert={convertLead} onArchive={archiveLead} onOpenCustomer={(cid) => setOpenCustomerId(cid)} busy={leadBusy} />
                  </PreviewShell>
                </div>
              </div>
            )}
          </div>
        ) : tab === "reputation" ? (
          hasModule("reputation") ? (
            <ReputationManagementTab
              onOpenCustomer={(cid) => setOpenCustomerId(cid)}
              onOpenJob={(jobId) => navigate(`/jobs?id=${jobId}`)}
              branding={branding}
            />
          ) : (
            <div className="bg-white rounded-xl border border-slate-200/80 p-12 flex flex-col items-center text-center" data-testid="reputation-locked">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3"><Lock className="h-6 w-6 text-slate-400" /></div>
              <h3 className="text-[15px] font-semibold text-slate-700">Reputation Management isn't in your plan</h3>
              <p className="text-[13px] text-slate-500 mt-1 max-w-sm">
                Upgrade to unlock star-rating review requests, a custom feedback form, and low-review alerts.{" "}
                <button onClick={() => navigate("/settings/billing")} data-testid="reputation-upgrade-link" className="text-blue-600 font-semibold hover:underline">
                  View Plans &amp; Pricing
                </button>
              </p>
            </div>
          )
        ) : null}
      </div>

      {/* Settings modal */}
      {settingsOpen && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={() => setSettingsOpen(false)} data-testid="marketing-settings-modal">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-semibold text-slate-900">Marketing settings</h3>
              <button onClick={() => setSettingsOpen(false)} className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4.5 w-4.5 text-slate-500" /></button>
            </div>
            <h4 className="text-[13.5px] font-semibold text-slate-800">Google review link</h4>
            <p className="text-[12.5px] text-slate-500 mt-1">Used by the <strong>{`{{reviewLink}}`}</strong> merge tag and the Review request template.</p>
            <input value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} data-testid="marketing-review-url" placeholder="https://g.page/r/your-business/review" className="mt-3 w-full h-9 px-3 rounded-md border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
            {!smtpConfigured && (
              <a href="/settings/company" className="mt-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 hover:bg-amber-100/60 transition-colors" data-testid="marketing-smtp-link">
                <span className="text-[13px] text-amber-800">Connect your email server to start sending campaigns</span>
                <ExternalLink className="h-4 w-4 text-amber-600" />
              </a>
            )}
            <div className="mt-4 flex justify-end">
              <Button onClick={saveSettings} disabled={savingSettings} data-testid="marketing-settings-save" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">{savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</Button>
            </div>
          </div>
        </div>
      )}

      <EmailBuilder open={editorOpen} onClose={() => { setEditorOpen(false); setStartWithAI(false); setEditCampaignId(null); refresh(); }} accentColor={branding.accentColor} customers={customers} smtpConfigured={smtpConfigured} defaultTestEmail={user?.email} onSaved={refresh} startWithAI={startWithAI} companyName={branding.companyName || ""} editCampaignId={editCampaignId} />
      <CampaignDetail campaignId={detailId} open={!!detailId} onClose={() => setDetailId(null)} smtpConfigured={smtpConfigured} onChanged={refresh} />
      <FormBuilder formId={formId} open={!!formId} onClose={() => { setFormId(null); refresh(); }} />
      <LeadsInbox form={leadsForm} open={!!leadsForm} onClose={() => { setLeadsForm(null); refresh(); if (tab === "leads") loadLeads(forms); }} onOpenCustomer={(cid) => setOpenCustomerId(cid)} />
      {openCustomerId && <CustomerProfileOverlay customerId={openCustomerId} onClose={() => setOpenCustomerId(null)} />}
    </>
  );
}
